/**
 * 對話頁彈窗（UI v2 步 5：由 `ChatPage.tsx` **純搬**出嚟，零行為改動）
 *
 *   · ① 揀語言／語體／風格 →〔開始〕→ 理解步驟（免費）
 *   · ③b 換風格（選項 ＋ 自由填寫）
 *   · 之前嘅對話（歷史；可以改名／刪）
 *   · 改名／刪對話（二次確認）
 *   · 〔加入播放清單〕
 *
 * ⚠️ 全部狀態／處理器由父層（`ChatPage`）傳入 —— 本檔只搬 JSX。
 */
import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import BottomSheet from '@coeldery/bottom-sheet';
import SessionList from '@coeldery/session-list';
import StylePicker, { type StyleOption } from '@coeldery/style-picker';
import AddToPlaylist from '../../components/AddToPlaylist';
import { DEFAULT_QUICK_GENRE_IDS } from '../../genres';
import type { HistoryItem, SheetKind, SongLang, SongRegister } from './types';
import { SHEET_PRIMARY } from './styles';

export interface ChatSheetsProps {
  t: TFunction;
  sheet: SheetKind;
  setSheet: (s: SheetKind) => void;
  busyAction: boolean;
  langBtn: (active: boolean) => string;
  /** ① 揀語言彈窗 */
  runUnderstand: () => void;
  songGenre: string | null;
  pickGenre: (id: string | null) => void;
  genreOptions: StyleOption[];
  genreQuick: string[];
  toggleQuick: (id: string) => void;
  songRegister: SongRegister;
  setSongRegister: (r: SongRegister) => void;
  registerOptions: { code: string; label: string }[];
  songLang: SongLang;
  setSongLang: (l: SongLang) => void;
  langOptions: { code: string; label: string }[];
  pinnedCount: number;
  /** ③b 換風格 */
  runRemake: () => void;
  styleOptions: StyleOption[];
  styleSelected: string[];
  setStyleSelected: Dispatch<SetStateAction<string[]>>;
  styleFree: string;
  setStyleFree: (v: string) => void;
  /** 之前嘅對話 */
  historyError: string | null;
  setHistoryError: (v: string | null) => void;
  history: HistoryItem[];
  sessionId: string;
  historyBusy: boolean;
  historyBusyId: string | null;
  openSession: (id: string) => void;
  setRenameTarget: Dispatch<SetStateAction<{ id: string; title: string } | null>>;
  setDeleteTarget: (v: HistoryItem | null) => void;
  /** 改名 */
  renameTarget: { id: string; title: string } | null;
  commitRename: () => void;
  /** 刪對話 */
  deleteTarget: HistoryItem | null;
  commitDelete: () => void;
  /** 加入播放清單 */
  playlistSongId: string | null;
  setPlaylistSongId: (v: string | null) => void;
  onLibraryReload: () => void;
}

export default function ChatSheets(props: ChatSheetsProps) {
  const {
    t, sheet, setSheet, busyAction, langBtn,
    runUnderstand, songGenre, pickGenre, genreOptions, genreQuick, toggleQuick,
    songRegister, setSongRegister, registerOptions, songLang, setSongLang, langOptions, pinnedCount,
    runRemake, styleOptions, styleSelected, setStyleSelected, styleFree, setStyleFree,
    historyError, setHistoryError, history, sessionId, historyBusy, historyBusyId, openSession, setRenameTarget, setDeleteTarget,
    renameTarget, commitRename, deleteTarget, commitDelete, playlistSongId, setPlaylistSongId, onLibraryReload,
  } = props;

  return (
    <>
      {/* ① 揀語言 →〔開始〕→ 理解步驟（免費） */}
      <BottomSheet
        open={sheet === 'lang'}
        titleKey="sheet_start_song_title"
        onClose={() => setSheet('none')}
        actions={
          <button
            type="button"
            data-testid="sheet-write"
            disabled={busyAction}
            onClick={() => void runUnderstand()}
            style={{ ...SHEET_PRIMARY, opacity: busyAction ? 0.6 : 1 }}
          >
            {busyAction ? t('chat_thinking') : t('understanding_start')}
          </button>
        }
      >
        {/* ⭐ 出歌音樂風格（0014；`decisions/034`）：預設**自動（AI 揀）**；撳〔自己揀〕才出清單 */}
        <p style={{ margin: '0 0 8px', fontSize: 'var(--font-size-small, 15px)', fontWeight: 700 }}>
          {t('genre_label')}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }} data-testid="genre-picker">
          <button
            type="button"
            data-testid="genre-auto"
            aria-pressed={songGenre === null}
            className={langBtn(songGenre === null)}
            disabled={busyAction}
            onClick={() => pickGenre(null)}
          >
            {t('genre_auto')}
          </button>
          <button
            type="button"
            data-testid="genre-self"
            aria-pressed={songGenre !== null}
            className={langBtn(songGenre !== null)}
            disabled={busyAction}
            onClick={() => pickGenre(songGenre ?? DEFAULT_QUICK_GENRE_IDS[0])}
          >
            {t('genre_self')}
          </button>
        </div>
        {songGenre === null ? (
          <p
            className="footnote"
            data-testid="genre-auto-note"
            style={{ color: 'var(--color-text-secondary)', margin: '0 0 10px' }}
          >
            {t('genre_auto_note')}
          </p>
        ) : (
          <div style={{ marginBottom: '10px' }}>
            <StylePicker
              options={genreOptions}
              selected={[songGenre]}
              onToggle={(id) => pickGenre(id)}
              freeText=""
              onFreeTextChange={() => {}}
              busy={busyAction}
              quickIds={genreQuick}
              onToggleQuick={toggleQuick}
              hideFree
            />
          </div>
        )}

        {/* ⭐ 語體（`decisions/030`）：市場書面（預設）／口語 */}
        <p style={{ margin: '0 0 8px', fontSize: 'var(--font-size-small, 15px)', fontWeight: 700 }}>
          {t('lyrics_sheet_pick_register')}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }} data-testid="register-picker">
          {registerOptions.map((opt) => (
            <button
              key={opt.code}
              type="button"
              data-testid={`register-${opt.code}`}
              aria-pressed={songRegister === opt.code}
              className={langBtn(songRegister === opt.code)}
              disabled={busyAction}
              onClick={() => setSongRegister(opt.code as SongRegister)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="footnote" style={{ color: 'var(--color-text-secondary)', margin: '0 0 8px' }}>
          {t('register_hint')}
        </p>
        <p style={{ margin: '0 0 8px', fontSize: 'var(--font-size-small, 15px)', fontWeight: 700 }}>
          {t('lyrics_sheet_pick_lang')}
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {langOptions.map((opt) => (
            <button
              key={opt.code}
              type="button"
              aria-pressed={songLang === opt.code}
              className={langBtn(songLang === opt.code)}
              disabled={busyAction}
              onClick={() => setSongLang(opt.code as SongLang)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        {pinnedCount > 0 ? (
          <p className="footnote" style={{ color: 'var(--color-text-secondary)' }}>
            {t('must_use_marked_count', { n: pinnedCount })}
          </p>
        ) : null}
      </BottomSheet>


      {/* ③b 換風格：選項 ＋ 自由填寫（`decisions/026`） */}
      <BottomSheet
        open={sheet === 'style'}
        titleKey="style_title"
        onClose={() => setSheet('none')}
        actions={
          <button
            type="button"
            data-testid="style-start"
            disabled={busyAction}
            onClick={() => void runRemake()}
            style={{ ...SHEET_PRIMARY, opacity: busyAction ? 0.6 : 1 }}
          >
            {busyAction ? t('chat_thinking') : t('style_start')}
          </button>
        }
      >
        <StylePicker
          options={styleOptions}
          selected={styleSelected}
          onToggle={(id) => setStyleSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))}
          freeText={styleFree}
          onFreeTextChange={setStyleFree}
          busy={busyAction}
        />
      </BottomSheet>

      {/* 之前嘅對話（一部機） */}
      <BottomSheet open={sheet === 'history'} titleKey="history_title" onClose={() => setSheet('none')}>
        {historyError ? (
          <p className="page__error" role="alert">
            {historyError}
          </p>
        ) : null}
        <SessionList
          items={history.map((h) => ({
            id: h.sessionId,
            title: h.title ?? null,
            preview: h.preview,
            meta: `${new Date(h.lastSeenAt).toLocaleDateString()} · ${t('history_songs', { n: h.songs })}`,
          }))}
          currentId={sessionId}
          busy={historyBusy}
          emptyKey="history_empty"
          untitledKey="history_untitled"
          busyId={historyBusyId}
          /** ⭐ 批次 C：改名／刪除**只出意圖**（彈窗同確認喺呢度做） */
          onRename={(id, currentTitle) => {
            setHistoryError(null);
            setRenameTarget({ id, title: currentTitle });
            setSheet('rename');
          }}
          onDelete={(id) => {
            setHistoryError(null);
            setDeleteTarget(history.find((h) => h.sessionId === id) ?? null);
            setSheet('delete');
          }}
          onPick={(id) => {
            if (id !== sessionId) openSession(id);
            setSheet('none');
          }}
        />
      </BottomSheet>


      {/* ⭐ 批次 C：改名（產品負責人：「giving it a name of the conversation」） */}
      <BottomSheet
        open={sheet === 'rename'}
        titleKey="history_rename_title"
        onClose={() => setSheet('none')}
        actions={
          <button
            type="button"
            data-testid="rename-save"
            disabled={historyBusyId !== null}
            onClick={() => void commitRename()}
            style={{ ...SHEET_PRIMARY, opacity: historyBusyId ? 0.6 : 1 }}
          >
            {t('history_save')}
          </button>
        }
      >
        <input
          data-testid="rename-input"
          value={renameTarget?.title ?? ''}
          maxLength={40}
          placeholder={t('history_name_placeholder')}
          onChange={(e) => setRenameTarget((prev) => (prev ? { ...prev, title: e.target.value } : prev))}
          style={{
            width: '100%',
            minHeight: '44px',
            padding: '8px 12px',
            borderRadius: '12px',
            border: '1.5px solid var(--color-divider)',
            background: 'var(--color-card)',
            color: 'var(--color-text)',
            fontFamily: 'inherit',
            fontSize: 'var(--font-size-small, 15px)',
          }}
        />
      </BottomSheet>

      {/* ⭐ 0013（`decisions/033`）：〔加入播放清單〕彈窗（分類 chips ＋ 開新清單） */}
      <AddToPlaylist
        open={playlistSongId !== null}
        songId={playlistSongId}
        onClose={() => setPlaylistSongId(null)}
        onChanged={onLibraryReload}
      />

      {/* ⭐ 批次 C：刪對話 —— **二次確認**，明文寫清楚「刪咗冇得返；歌會留喺我嘅歌」 */}
      <BottomSheet
        open={sheet === 'delete'}
        titleKey="history_delete_title"
        onClose={() => setSheet('none')}
        actions={
          <>
            <button
              type="button"
              data-testid="delete-cancel"
              onClick={() => setSheet('none')}
              style={{ ...SHEET_PRIMARY, background: 'var(--color-card)', color: 'var(--color-text)', border: '1.5px solid var(--color-divider)' }}
            >
              {t('history_cancel')}
            </button>
            <button
              type="button"
              data-testid="delete-confirm"
              disabled={historyBusyId !== null}
              onClick={() => void commitDelete()}
              style={{ ...SHEET_PRIMARY, marginTop: '8px', background: 'var(--color-danger)', borderColor: 'var(--color-danger)', color: 'var(--color-on-primary)' }}
            >
              {t('history_delete_confirm')}
            </button>
          </>
        }
      >
        <p style={{ margin: '0 0 6px', fontWeight: 700 }}>
          {deleteTarget?.title?.trim() || deleteTarget?.preview || t('history_untitled')}
        </p>
        <p className="footnote" data-testid="delete-warning" style={{ margin: 0 }}>
          {t('history_delete_warning')}
        </p>
      </BottomSheet>
    </>
  );
}

